import { auth } from './firebase';
import { getAnalytics, logEvent } from 'firebase/analytics';

const API_BASE = '/.netlify/functions';

/**
 * Subscription Service
 * Handles all subscription-related API calls and analytics
 */
export class SubscriptionService {
  /**
   * Create a Stripe Checkout session for subscription
   * @param {string} promoCode - Optional promo/coupon code
   * @returns {Promise<{sessionId: string, url: string}>}
   */
  static async createCheckoutSession(promoCode = null) {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User must be logged in to subscribe');
      }

      const token = await user.getIdToken();
      
      const response = await fetch(`${API_BASE}/stripe-create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          promoCode,
          successUrl: `${window.location.origin}/subscription?success=true`,
          cancelUrl: `${window.location.origin}/subscription?canceled=true`
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create checkout session');
      }

      const data = await response.json();

      // Log analytics event
      SubscriptionService.logAnalyticsEvent('checkout_initiated', {
        promo_code: promoCode || 'none'
      });

      return data;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw error;
    }
  }

  /**
   * Get current subscription status for the logged-in user
   * @returns {Promise<Object>} Subscription data
   */
  static async getSubscriptionStatus() {
    try {
      const user = auth.currentUser;
      if (!user) {
        return {
          isPremium: false,
          subscriptionStatus: null,
          hasActiveSubscription: false
        };
      }

      const token = await user.getIdToken();

      const response = await fetch(`${API_BASE}/stripe-get-subscription`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to get subscription status');
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting subscription status:', error);
      throw error;
    }
  }

  /**
   * Open Stripe Customer Portal for subscription management
   * @returns {Promise<void>} Redirects to Stripe portal
   */
  static async openCustomerPortal() {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User must be logged in to access billing portal');
      }

      const token = await user.getIdToken();

      const response = await fetch(`${API_BASE}/stripe-customer-portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          returnUrl: `${window.location.origin}/subscription`
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to open customer portal');
      }

      const data = await response.json();

      // Log analytics event
      SubscriptionService.logAnalyticsEvent('portal_opened');

      // Redirect to Stripe portal
      window.location.href = data.url;
    } catch (error) {
      console.error('Error opening customer portal:', error);
      throw error;
    }
  }

  /**
   * Validate a promo code before checkout
   * @param {string} promoCode - The promo code to validate
   * @returns {Promise<boolean>} Whether the promo code is valid
   */
  static async validatePromoCode(promoCode) {
    // For now, we'll validate during checkout
    // This could be expanded with a dedicated endpoint if needed
    if (!promoCode || promoCode.trim().length === 0) {
      return false;
    }
    return true; // Actual validation happens at checkout
  }

  /**
   * Get subscription metrics for admin dashboard
   * @returns {Promise<Object>} Subscription metrics
   */
  static async getSubscriptionMetrics() {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User must be logged in');
      }

      const token = await user.getIdToken();

      const response = await fetch(`${API_BASE}/stripe-get-subscription-metrics`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to get subscription metrics');
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting subscription metrics:', error);
      throw error;
    }
  }

  /**
   * Log analytics event to Firebase Analytics
   * @param {string} eventName - Name of the event
   * @param {Object} params - Event parameters
   */
  static logAnalyticsEvent(eventName, params = {}) {
    try {
      const analytics = getAnalytics();
      logEvent(analytics, `subscription_${eventName}`, {
        ...params,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      // Analytics might not be available in some environments
      console.warn('Analytics event logging failed:', error);
    }
  }

  /**
   * Log subscription started event
   * @param {string} tier - Subscription tier
   * @param {boolean} isTrial - Whether this is a trial
   */
  static logSubscriptionStarted(tier = 'premium', isTrial = false) {
    SubscriptionService.logAnalyticsEvent('started', {
      tier,
      is_trial: isTrial
    });
  }

  /**
   * Log subscription cancelled event
   */
  static logSubscriptionCancelled() {
    SubscriptionService.logAnalyticsEvent('cancelled');
  }

  /**
   * Log trial started event
   */
  static logTrialStarted() {
    SubscriptionService.logAnalyticsEvent('trial_started');
  }

  /**
   * Log trial converted event (trial user became paying customer)
   */
  static logTrialConverted() {
    SubscriptionService.logAnalyticsEvent('trial_converted');
  }
}

export default SubscriptionService;
